import { useState, useEffect, useCallback, useMemo } from 'react';
import RecordTypeService from 'views/RecordType/service/index.js';

/**
 * @typedef {Object} RecordType
 * @property {string} id
 * @property {string} name
 * @property {boolean} active
 * @property {string} entity
 * @property {string|null} lead_process_id
 * @property {string|null} sales_process_id
 * @property {string|null} protocol_process_id
 * @property {boolean|null} accepts_duplicates_creation
 * @property {boolean} is_sla_active
 * @property {boolean} should_send_notification_to_supervisor
 * @property {boolean} should_send_notification_to_attendant
 * @property {boolean} should_send_notification_to_client
 * @property {boolean} movement_alert
 * @property {boolean} sla_time_alert
 * @property {boolean} opening_or_closing_alert
 */

/**
 * @typedef {Object} SelectableOption
 * @property {string} label
 * @property {string} value
 */

/**
 * Hook customizado para obter os tipos de registro.
 * @param {string} entity - A entidade para a qual os tipos de registro são buscados.
 * @returns {{
 *   recordTypes: RecordType[] | null,
 *   isLoadingRecordTypes: boolean,
 *   getRecordTypes: (params?: Object) => void,
 *   clearRecordTypes: () => void,
 *   recordTypesOptions: SelectableOption[]
 * }}
 */
export const useRecordTypesByEntity = (entity) => {
  /** @type {[RecordType[], React.Dispatch<React.SetStateAction<RecordType[]>>]} */
  const [recordTypes, setRecordTypes] = useState([]);
  const [isLoadingRecordTypes, setIsLoadingRecordTypes] = useState(false);

  /**
   * Todos os tipos de registro como uma SelectableOption
   * @type {SelectableOption[]}
   */
  const recordTypesOptions = useMemo(() => {
    return recordTypes.map((type) => ({ label: type.name, value: type.id }));
  }, [recordTypes]);

  /**
   * Busca tipos de registro do servidor.
   * @param {Object} [params={}]
   */
  const getRecordTypes = useCallback(
    async (params = {}) => {
      if (!entity) {
        console.log(
          '[useRecordTypes]: o tipo de entidade não foi informado. Nenhum registro será retornado. Certifique-se de enviar o parâmetro entity durante a chamada do hook.'
        );
        return;
      }

      setIsLoadingRecordTypes(true);
      params.entity = entity;

      RecordTypeService.list(params)
        .then((response) => {
          const recordTypeData = response.data.recordTypes;
          setRecordTypes(recordTypeData);
        })
        .catch(() => {
          setRecordTypes([]);
        })
        .finally(() => {
          setIsLoadingRecordTypes(false);
        });
    },
    [entity]
  );

  /**
   * Limpa os dados dos tipos de registro.
   */
  const clearRecordTypes = useCallback(() => {
    setRecordTypes([]);
    setIsLoadingRecordTypes(false);
  }, []);

  useEffect(() => {
    getRecordTypes();
  }, [getRecordTypes]);

  return {
    recordTypes,
    isLoadingRecordTypes,
    getRecordTypes,
    clearRecordTypes,
    recordTypesOptions
  };
};
